import { t } from 'i18next';
import { WEBSHOP_ENABLED } from '../../constants/Settings';
import { StrapiUser } from '../../core/models/strapi-user.model';
import { getFirstVendor } from '../../utils/userUtils';

export type AccountMenuAction = 'cookieSettings' | 'logout';

export type AccountMenuItem = {
  id: string;
  label: string;
  icon: string;
  // A menu entry either navigates (`to`) or runs an action — never both.
  to?: string;
  action?: AccountMenuAction;
  showUnreadBadge?: boolean;
};

// The one definition behind every account menu: the header dropdown on desktop,
// the same dropdown inside the mobile hamburger, and the account icon menu in
// the mobile icon bar. They used to be three hand-kept copies and had drifted
// apart in order, labels and icons.
//
// Labels are resolved here, with literal `t('…')` calls, rather than handed to
// the consumers as key strings: `yarn gen:i18n` runs with `keepRemoved: false`
// and only sees literal keys, so a key travelling as a data field gets pruned
// out of the catalogue on the next extraction (which is how `navbar.myShop`
// went missing). This module is not a component, so the module-level `t` is the
// right source — it reads the current language at call time, and the consumers'
// own `useTranslation()` re-renders them on a language change.
export const getAccountMenuItems = (user: StrapiUser | null): AccountMenuItem[] => {
  const vendor = getFirstVendor(user);
  // A user without a vendor still carries a placeholder record with id 0.
  const ownVendor = vendor && vendor.id !== 0 ? vendor : null;

  const vendorItems: AccountMenuItem[] = ownVendor
    ? [
        {
          id: 'vendorData',
          label: t('navbar.vendorData'),
          icon: 'home',
          to: '/myaccount/vendorregistration'
        },
        {
          id: 'shop',
          label: t('navbar.myShop'),
          icon: 'store',
          to: `/haendler/${ownVendor.urlSegment}`
        }
      ]
    : [];

  const orderItems: AccountMenuItem[] = WEBSHOP_ENABLED
    ? [{ id: 'orders', label: t('navbar.orders'), icon: 'clipboard-list', to: '/myaccount/orders' }]
    : [];

  const salesItems: AccountMenuItem[] =
    WEBSHOP_ENABLED && ownVendor
      ? [
          {
            id: 'vendorOrders',
            label: t('navbar.mySales'),
            icon: 'box',
            to: '/myaccount/vendor-orders'
          }
        ]
      : [];

  return [
    { id: 'account', label: t('navbar.myAccount'), icon: 'user', to: '/myaccount/' },
    ...vendorItems,
    ...orderItems,
    ...salesItems,
    {
      id: 'bookmarks',
      label: t('navbar.bookmarks'),
      icon: 'certificate',
      to: '/myaccount/bookmarks'
    },
    {
      id: 'messages',
      label: t('navbar.messages'),
      icon: 'comment-alt',
      to: '/myaccount/messages',
      showUnreadBadge: true
    },
    {
      id: 'cookieSettings',
      label: t('navbar.cookieSettings'),
      icon: 'cookie',
      action: 'cookieSettings'
    },
    { id: 'logout', label: t('navbar.logout'), icon: 'sign-out-alt', action: 'logout' }
  ];
};
